// Batch rollup strip (transcripts.md): when `?batch=` is active the filtered
// list gets one collapsed identity row (name · status Badge · capacity-wait ·
// loaded counts · Details/Clear) with an expandable KeyValue 2-up — never a
// second stacked table (batches are a facet of /transcripts per docs/09).
// BatchResponse carries only UUIDs, so the expanded pane resolves labels via
// three reads `enabled` ONLY while expanded (docs/09 §6 bounded actor
// resolution); any miss/error falls back to the short resource id. Container
// (queries + expand state) / View (props-pure, story-testable) split.

import { useRef, useState, type ReactNode, type SyntheticEvent } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { ApiRequestError, requestErrorDescription } from "../../lib/errors";
import { formatDateTime } from "../../lib/format";
import { queryKeys } from "../../lib/queryKeys";
import { shortResourceId } from "../../lib/shellNavigation";
import { isShowcaseMode } from "../../lib/showcaseMode";
import {
  Badge,
  Button,
  Copyable,
  Divider,
  Grid,
  Inline,
  InlineError,
  KeyValue,
  Popover,
  Skeleton,
  Stack,
  Text,
  TextInput,
  useToast,
} from "../../ui";
import { interfaceLabel } from "../agent/lib/connectionConfig";
import { CapacityWaitStatus } from "../CapacityWaitStatus";
import {
  userDisplayName,
  userMap,
  useUserDirectoryQuery,
} from "../shared/userDirectory";

type BatchResponse = components["schemas"]["BatchResponse"];

export interface BatchStatusCounts {
  failed: number;
  pending: number;
  processing: number;
  ready: number;
  total: number;
}

export function batchStatusTone(
  status: BatchResponse["status"],
): "danger" | "neutral" | "success" {
  if (status === "failed") {
    return "danger";
  }
  if (status === "done") {
    return "success";
  }
  return "neutral";
}

// "14 ready · 2 processing of 17 loaded" — counts the LOADED transcript rows
// by status (BatchResponse carries no per-status counts; honest, zero new
// endpoints — see transcripts.md open items).
export function loadedCountsLabel(counts: BatchStatusCounts): string {
  if (counts.total === 0) {
    return "no transcripts loaded";
  }
  const parts = (
    [
      ["ready", counts.ready],
      ["processing", counts.processing],
      ["pending", counts.pending],
      ["failed", counts.failed],
    ] as const
  )
    .filter(([, count]) => count > 0)
    .map(([status, count]) => `${String(count)} ${status}`);
  return `${parts.join(" · ")} of ${String(counts.total)} loaded`;
}

export interface BatchRollupStripProps {
  batch: BatchResponse | undefined;
  errorMessage?: string;
  isPending: boolean;
  loadedCounts: BatchStatusCounts;
  // Drops `?batch` (replace).
  onClearBatch: () => void;
}

export function BatchRollupStrip({
  batch,
  errorMessage,
  isPending,
  loadedCounts,
  onClearBatch,
}: BatchRollupStripProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [isExpanded, setIsExpanded] = useState(false);
  const resolveDetails = isExpanded && batch != null;

  const launchedById = batch?.launched_by ?? null;
  const users = useUserDirectoryQuery(
    resolveDetails && launchedById != null ? launchedById : "",
    "batch-rollup-strip",
  );
  const agentVersion = useQuery({
    enabled: resolveDetails,
    queryKey: queryKeys.agentVersions.detail(batch?.agent_version_id ?? ""),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/agent-versions/{id}", {
        params: { path: { id: batch?.agent_version_id ?? "" } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });
  const connection = useQuery({
    enabled: resolveDetails,
    queryKey: queryKeys.connections.detail(batch?.connection_id ?? ""),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/connections/{id}", {
        params: { path: { id: batch?.connection_id ?? "" } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });
  const renameBatch = useMutation({
    mutationFn: async (displayName: string) => {
      if (batch == null) {
        throw new Error("Batch could not be loaded.");
      }
      const { data, error } = await apiClient.PATCH("/v1/batches/{id}", {
        body: { display_name: displayName },
        params: { path: { id: batch.id } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async (renamed) => {
      queryClient.setQueryData(queryKeys.batches.detail(renamed.id), renamed);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.batches.list(),
      });
      toast.success("Batch renamed.");
    },
  });

  const launchedByUser =
    launchedById != null
      ? userMap(users.data?.items ?? []).get(launchedById)
      : undefined;

  return (
    <BatchRollupStripView
      batch={batch}
      connectionLabel={
        connection.data != null
          ? `${connection.data.display_name} (${interfaceLabel(connection.data)})`
          : batch != null && (
              <Copyable label="Interface ID" value={batch.connection_id} />
            )
      }
      errorMessage={errorMessage}
      isExpanded={isExpanded}
      isPending={isPending}
      isRenamePending={renameBatch.isPending}
      launchedByLabel={
        launchedById == null ? (
          "system"
        ) : launchedByUser != null ? (
          userDisplayName(launchedByUser)
        ) : (
          <Copyable label="User ID" value={launchedById} />
        )
      }
      loadedCounts={loadedCounts}
      versionLabel={
        agentVersion.data != null
          ? agentVersion.data.tag
          : batch != null && (
              <Copyable
                label="Agent version ID"
                value={batch.agent_version_id}
              />
            )
      }
      onClearBatch={onClearBatch}
      onRename={async (displayName) => {
        await renameBatch.mutateAsync(displayName);
      }}
      onToggleExpanded={() => {
        setIsExpanded((current) => !current);
      }}
    />
  );
}

export interface BatchRollupStripViewProps {
  batch: BatchResponse | undefined;
  connectionLabel: ReactNode;
  errorMessage?: string;
  isExpanded: boolean;
  isPending: boolean;
  isRenamePending: boolean;
  launchedByLabel: ReactNode;
  loadedCounts: BatchStatusCounts;
  versionLabel: ReactNode;
  onClearBatch: () => void;
  onRename: (displayName: string) => Promise<void>;
  onToggleExpanded: () => void;
}

export function BatchRollupStripView({
  batch,
  connectionLabel,
  errorMessage,
  isExpanded,
  isPending,
  isRenamePending,
  launchedByLabel,
  loadedCounts,
  versionLabel,
  onClearBatch,
  onRename,
  onToggleExpanded,
}: BatchRollupStripViewProps) {
  const renameAnchorRef = useRef<HTMLButtonElement>(null);
  const [showRenameForm, setShowRenameForm] = useState(false);

  if (batch == null) {
    return (
      <BatchRollupStripFrame>
        {isPending ? (
          <Skeleton label="Loading batch" rows={1} shape="table-row" />
        ) : (
          <Inline align="center" gap={3}>
            <InlineError>
              {errorMessage ?? "Batch could not be loaded."}
            </InlineError>
            <Button size="sm" variant="secondary" onClick={onClearBatch}>
              Clear batch
            </Button>
          </Inline>
        )}
      </BatchRollupStripFrame>
    );
  }

  return (
    <BatchRollupStripFrame>
      <Stack gap={3}>
        <Inline align="center" gap={3}>
          <Text as="span">
            {batch.display_name ?? shortResourceId(batch.id)}
          </Text>
          <Badge tone={batchStatusTone(batch.status)}>{batch.status}</Badge>
          <CapacityWaitStatus
            isWaitingForCapacity={batch.is_waiting_for_capacity}
            capacityWaitCause={batch.capacity_wait_cause}
          />
          <Text as="span" variant="muted">
            {loadedCountsLabel(loadedCounts)}
          </Text>
          <Button
            aria-expanded={isExpanded}
            size="sm"
            variant="secondary"
            onClick={onToggleExpanded}
          >
            Details
          </Button>
          {isShowcaseMode ? null : (
            <div className="marker-popover-anchor">
              <Button
                ref={renameAnchorRef}
                disabled={isRenamePending}
                size="sm"
                variant="secondary"
                onClick={() => {
                  setShowRenameForm(true);
                }}
              >
                Rename
              </Button>
              <Popover
                align="end"
                anchorRef={renameAnchorRef}
                aria-label="Rename batch"
                isOpen={showRenameForm}
                onDismiss={() => {
                  setShowRenameForm(false);
                }}
              >
                <BatchRenameForm
                  key={`${batch.id}:${batch.display_name ?? ""}`}
                  batch={batch}
                  isPending={isRenamePending}
                  onCancel={() => {
                    setShowRenameForm(false);
                  }}
                  onRename={onRename}
                />
              </Popover>
            </div>
          )}
          <Button size="sm" variant="secondary" onClick={onClearBatch}>
            Clear batch
          </Button>
        </Inline>
        {/* ASSUMED(rerun counts belong in the active-batch strip). */}
        {batch.comparison != null ? (
          <Inline align="center" gap={3}>
            <Text as="span" variant="muted">
              Compared with parent
            </Text>
            <Badge tone="danger">
              Regressed {batch.comparison.regressed_transcript_ids?.length ?? 0}
            </Badge>
            <Badge tone="success">
              Improved {batch.comparison.improved_transcript_ids?.length ?? 0}
            </Badge>
            <Badge tone="warning">
              Changed to warn {batch.comparison.changed_to_warn}
            </Badge>
            <Badge tone="warning">
              Changed from warn {batch.comparison.changed_from_warn}
            </Badge>
          </Inline>
        ) : null}
        {isExpanded ? (
          <Grid columns={2} gap={3}>
            <KeyValue
              rows={[
                { label: "Created", value: formatDateTime(batch.created_at) },
                { label: "Launched by", value: launchedByLabel },
                { label: "Agent version", value: versionLabel },
                { label: "Connection", value: connectionLabel },
              ]}
            />
            <KeyValue
              rows={[
                {
                  label: "Scenario set",
                  value: (
                    <Copyable
                      label="Scenario set ID"
                      value={batch.scenario_set_id}
                    />
                  ),
                },
                {
                  label: "Seed",
                  value: batch.seed == null ? "-" : String(batch.seed),
                },
                {
                  label: "id",
                  value: <Copyable label="Batch ID" value={batch.id} />,
                },
              ]}
            />
          </Grid>
        ) : null}
      </Stack>
    </BatchRollupStripFrame>
  );
}

interface BatchRenameFormProps {
  batch: BatchResponse;
  isPending: boolean;
  onCancel: () => void;
  onRename: (displayName: string) => Promise<void>;
}

function BatchRenameForm({
  batch,
  isPending,
  onCancel,
  onRename,
}: BatchRenameFormProps) {
  const [displayName, setDisplayName] = useState(batch.display_name ?? "");
  const [localError, setLocalError] = useState<string | null>(null);
  const normalizedName = displayName.trim();
  const isDisabled =
    isPending ||
    normalizedName.length === 0 ||
    normalizedName === batch.display_name;

  async function handleSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isDisabled) {
      return;
    }
    setLocalError(null);
    try {
      await onRename(normalizedName);
      onCancel();
    } catch (caught) {
      setLocalError(requestErrorDescription(caught));
    }
  }

  return (
    <Stack gap={3}>
      <Text variant="caps">Rename batch</Text>
      <form
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <Stack gap={3}>
          <TextInput
            label="Display name"
            required
            value={displayName}
            onChange={(event) => {
              setDisplayName(event.currentTarget.value);
            }}
          />
          {localError ? <InlineError>{localError}</InlineError> : null}
          <Inline>
            <Button disabled={isDisabled} type="submit">
              {isPending ? "Saving..." : "Save"}
            </Button>
            <Button
              disabled={isPending}
              type="button"
              variant="secondary"
              onClick={onCancel}
            >
              Cancel
            </Button>
          </Inline>
        </Stack>
      </form>
    </Stack>
  );
}

// One hairline below the strip row separates it from the table chrome
// (UI-LAW §1: grouping by whitespace + a single hairline, never a box).
function BatchRollupStripFrame({ children }: { children: ReactNode }) {
  return (
    <Stack gap={3}>
      {children}
      <Divider />
    </Stack>
  );
}
